import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertTriangle, Upload } from 'lucide-react';

const DRUG_NAMES = {
  AMP: 'Amphetamines', mAMP: 'Methamphetamines', MDMA: 'MDMA / Ecstasy',
  THC: 'Cannabis (THC)', COC: 'Cocaine', OPI: 'Opiates',
  BZO: 'Benzodiazepines', MTD: 'Methadone',
};

export default function PathologyUploadPage() {
  const { token } = useParams();
  const sigRef = useRef(null);
  const drawingRef = useRef(false);

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    lab_name: '',
    lab_provider_number: '',
    collector_name: '',
    witness_name: '',
    collection_time: new Date().toISOString().slice(0, 16),
    specimen_id: '',
    temperature_check: 'in_range',
    notes: '',
  });
  const [results, setResults] = useState({});  // drug -> 'not_detected' | 'detected'
  const [pdfData, setPdfData] = useState({ base64: '', name: '' });

  const fetchContext = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pathology/upload/${token}/context`);
      if (r.ok) {
        const d = await r.json();
        setCtx(d);
        // Default every drug to "not_detected"
        const init = {};
        (d.ordered_drugs || []).forEach(c => { init[c] = 'not_detected'; });
        setResults(init);
      } else {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || 'Invalid QR code');
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchContext(); }, [fetchContext]);

  // signature pad
  useEffect(() => {
    const c = sigRef.current;
    if (!c) return;
    const g = c.getContext('2d');
    g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
    g.lineWidth = 2; g.lineCap = 'round'; g.strokeStyle = '#111';
    let lx = 0, ly = 0;
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: (t.clientX - r.left) * c.width / r.width, y: (t.clientY - r.top) * c.height / r.height };
    };
    const start = (e) => { e.preventDefault(); drawingRef.current = true; const p = pos(e); lx = p.x; ly = p.y; };
    const move = (e) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const p = pos(e);
      g.beginPath(); g.moveTo(lx, ly); g.lineTo(p.x, p.y); g.stroke();
      lx = p.x; ly = p.y;
    };
    const end = () => { drawingRef.current = false; };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move); c.addEventListener('mouseup', end); c.addEventListener('mouseleave', end);
    c.addEventListener('touchstart', start); c.addEventListener('touchmove', move); c.addEventListener('touchend', end);
    return () => {
      c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move); c.removeEventListener('mouseup', end); c.removeEventListener('mouseleave', end);
      c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move); c.removeEventListener('touchend', end);
    };
  }, [ctx]);

  const onPdfChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { alert('PDF too large (max 8 MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setPdfData({ base64: reader.result, name: f.name });
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.lab_name || !form.collector_name || !form.specimen_id) {
      alert('Lab name, collector name and specimen ID are required.');
      return;
    }
    const sig = sigRef.current.toDataURL('image/png');
    setSubmitting(true);
    const body = {
      ...form,
      collection_time: new Date(form.collection_time).toISOString(),
      collector_signature_base64: sig,
      per_drug_results: results,
      pdf_base64: pdfData.base64 || null,
      pdf_filename: pdfData.name || null,
    };
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pathology/upload/${token}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSubmitting(false);
    if (r.ok) setDone(await r.json());
    else { const d = await r.json().catch(() => ({})); alert(d.detail || 'Submission failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-500" /></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center" data-testid="upload-invalid">
        <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-2" />
        <h1 className="text-xl font-semibold text-stone-900">QR not recognised</h1>
        <p className="text-stone-600 mt-2 text-sm">{error}</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center" data-testid="upload-success">
        <ShieldCheck className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
        <h1 className="text-xl font-semibold text-stone-900">Result submitted</h1>
        <p className="text-stone-600 mt-2 text-sm">{done.message}</p>
        {done.chain_of_custody_complete && <p className="text-emerald-700 mt-2 text-xs font-semibold">Chain-of-custody complete ✓</p>}
        <p className="text-xs text-stone-400 mt-4 font-mono">{done.result_id}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="pathology-upload-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-stone-900">Pathology Result Upload</h1>
        <p className="text-stone-600 mt-1 text-sm">Axiom Recovery Programme · No login required · Single-use QR</p>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 mt-4">
          <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-3">Patient identity verification</h2>
          <div className="flex items-center gap-4">
            {ctx.patient.photo && !ctx.patient.photo.startsWith('emoji:') && (
              <img src={ctx.patient.photo} alt="" className="w-16 h-16 rounded-full object-cover border border-stone-300" />
            )}
            <div>
              <div className="text-base font-medium text-stone-900">{ctx.patient.name}</div>
              <div className="text-xs text-stone-500">Verify photo ID matches before collecting specimen</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-stone-600 space-y-1">
            <div><b>Approving doctor:</b> {ctx.approving_doctor.name} (AHPRA {ctx.approving_doctor.ahpra_number}, Provider {ctx.approving_doctor.provider_number})</div>
            <div><b>Approved:</b> {ctx.approving_doctor.approved_at?.slice(0, 16)?.replace('T', ' ')} UTC</div>
            <div><b>Reason:</b> {ctx.reason}{ctx.court_date ? ` · court ${ctx.court_date}` : ''}</div>
            <div><b>Valid until:</b> {ctx.expires_at?.slice(0, 16)?.replace('T', ' ')}</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 mt-4 space-y-4">
          <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">Chain of custody</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Lab name *</label>
              <input data-testid="lab-name-input" value={form.lab_name} onChange={e => setForm({ ...form, lab_name: e.target.value })}
                placeholder="e.g. Australian Clinical Labs - Brisbane Central"
                className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Lab Provider number</label>
              <input data-testid="lab-provider-input" value={form.lab_provider_number} onChange={e => setForm({ ...form, lab_provider_number: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Collector name *</label>
              <input data-testid="collector-name-input" value={form.collector_name} onChange={e => setForm({ ...form, collector_name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Witness name</label>
              <input value={form.witness_name} onChange={e => setForm({ ...form, witness_name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Specimen ID *</label>
              <input data-testid="specimen-id-input" value={form.specimen_id} onChange={e => setForm({ ...form, specimen_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm font-mono" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Collection time *</label>
              <input type="datetime-local" value={form.collection_time} onChange={e => setForm({ ...form, collection_time: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Specimen temperature</label>
              <select value={form.temperature_check} onChange={e => setForm({ ...form, temperature_check: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm">
                <option value="in_range">In range (32-38 °C)</option>
                <option value="out_of_range">Out of range</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Collector signature *</label>
            <div className="border border-stone-300 rounded-lg overflow-hidden bg-white">
              <canvas ref={sigRef} width={600} height={140} className="w-full touch-none bg-white" data-testid="collector-sig-canvas" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 mt-4">
          <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-3">Drug panel result (8-panel standard court)</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {(ctx.ordered_drugs || []).map(code => (
              <div key={code} className="flex items-center justify-between border border-stone-200 rounded-lg p-2" data-testid={`result-row-${code}`}>
                <div>
                  <div className="text-sm font-medium text-stone-900">{DRUG_NAMES[code] || code}</div>
                  <div className="text-[10px] text-stone-500 font-mono">{code}</div>
                </div>
                <select data-testid={`result-${code}`}
                  value={results[code] || 'not_detected'}
                  onChange={e => setResults({ ...results, [code]: e.target.value })}
                  className="px-3 py-1 rounded-full border border-stone-300 bg-white text-xs">
                  <option value="not_detected">Not detected</option>
                  <option value="detected">Detected</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 mt-4">
          <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-3">Official lab result PDF</h2>
          <label className="inline-flex items-center gap-2 px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg text-sm text-stone-700 cursor-pointer" data-testid="pdf-upload-label">
            <Upload className="w-4 h-4" /> Choose lab result PDF
            <input type="file" accept="application/pdf" onChange={onPdfChange} className="hidden" data-testid="pdf-upload-input" />
          </label>
          {pdfData.name && <span className="text-xs text-stone-600 ml-3">{pdfData.name}</span>}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mt-3 mb-1">Notes (optional)</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm" />
          </div>
        </div>

        <button data-testid="pathology-submit"
          onClick={submit} disabled={submitting}
          className="w-full mt-5 px-6 py-3 bg-stone-900 text-white rounded-full text-sm font-medium hover:bg-stone-800 disabled:opacity-50">
          {submitting ? <Loader2 className="w-4 h-4 inline animate-spin" /> : 'Submit pathology result'}
        </button>
        <p className="text-xs text-stone-500 mt-2 text-center">Once submitted, this QR code becomes invalid.  Results flow back to the patient's app and treating clinician automatically.</p>
      </div>
    </div>
  );
}
